import { useEffect, createContext, type PropsWithChildren, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { GET_USER_DATA } from '../../config/apiPaths';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls, processApiPath } from '../../utils/url';

export interface UserProviderProps {
    userId: string | null;
    userUuid: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface UserContextInterface {
    userId: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    userData: any;
    setUserData: (userData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
}

const UserContext = createContext(undefined as unknown as UserContextInterface);

const { Provider, Consumer } = UserContext;

const UserProvider = (props: PropsWithChildren<UserProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, userId, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState<any>({ loading: true, userData: {} });
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('userProvider userId', userId);

    useEffect(() => {
        if (!userUuid || !userId) {
            return;
        }

        const fetchMe = async () => {
            try {
                const { data: responseData } = await axios.get(
                    processApiPath(GET_USER_DATA, undefined, {
                        user_id: userId,
                        auth_mode: authMode,
                        demo_content: String(showDemoContent)
                    }),
                    { headers: getUserUuidHeaders(userUuid) }
                );
                const getMeResponse = responseData.response_too_large_url
                    ? await axios.get(responseData.response_too_large_url)
                    : { data: responseData };
                setMe(getMeResponse);
                setMeFetched(true);
                setUserData(getMeResponse.data);
                setIsLoading(false);
            } catch (err) {
                setErrorMessage(err!.toString());
                setUserData(undefined);
                setIsLoading(false);
            }
        };

        fetchMe();
    }, [api, userId, authMode, userUuid, showDemoContent]);

    return (
        <Provider
            value={{
                userId,
                loading: userData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                userData,
                setUserData,
                me,
                meFetched,
                errorMessage
            }}
        >
            {children}
        </Provider>
    );
};

export { UserContext, UserProvider, Consumer as UserConsumer };
